import { useEffect, useMemo } from "react";
import { useGLTF } from "@react-three/drei";
import { RigidBody } from "@react-three/rapier";
import * as THREE from "three";

export default function Stairs({
  position = [-9, 13, -12.5],
  rotation = [0, 0, 0],
  scale = 1,
  ...rigidBodyProps
}) {
  const { scene } = useGLTF("/stairs.glb");
  const stairsScene = useMemo(() => scene.clone(true), [scene]);

  useEffect(() => {
    const stepColors = ["#008DF6", "#4DE284", "#F8C52C", "#F06968"];
    const boundingBox = new THREE.Box3();
    const size = new THREE.Vector3();
    const stairMeshes = [];
    let centralCylinder = null;

    const applyColor = (mesh, color) => {
      if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map((material) => {
          if (!material) return material;
          const cloned = material.clone();
          if (cloned.color) {
            cloned.color = cloned.color.clone();
            cloned.color.set(color);
          }
          return cloned;
        });
        return;
      }

      if (!mesh.material) return;

      const cloned = mesh.material.clone();
      if (cloned.color) {
        cloned.color = cloned.color.clone();
        cloned.color.set(color);
      }
      mesh.material = cloned;
    };

    stairsScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      child.castShadow = true;
      child.receiveShadow = true;

      boundingBox.setFromObject(child);
      boundingBox.getSize(size);

      const maxExtent = Math.max(size.x, size.y, size.z);
      const minExtent = Math.min(size.x, size.y, size.z);
      const isLikelyStep =
        size.y <= size.x &&
        size.y <= size.z &&
        size.y < maxExtent * 0.5 &&
        minExtent > 0;

      if (isLikelyStep) {
        stairMeshes.push(child);
        return;
      }

      const horizontalExtent = Math.max(size.x, size.z);
      const horizontalDifference = Math.abs(size.x - size.z);
      const horizontalUniform = horizontalExtent > 0 ? horizontalDifference / horizontalExtent < 0.15 : false;
      const verticallyDominant = size.y > size.x * 1.2 && size.y > size.z * 1.2;

      if (!centralCylinder && verticallyDominant && horizontalUniform && minExtent > 0) {
        centralCylinder = child;
      }
    });

    stairMeshes
      .sort((a, b) => {
        if (a.position.y !== b.position.y) return a.position.y - b.position.y;
        if (a.position.z !== b.position.z) return a.position.z - b.position.z;
        return a.position.x - b.position.x;
      })
      .forEach((mesh, index) => {
        const color = stepColors[index % stepColors.length];
        applyColor(mesh, color);
      });

    if (centralCylinder) {
      applyColor(centralCylinder, "#DB67D5");
    }
  }, [stairsScene]);

  return (
    <RigidBody
      type="fixed"
      colliders="trimesh"
      position={position}
      rotation={rotation}
      {...rigidBodyProps}
    >
      <primitive object={stairsScene} scale={scale} />
    </RigidBody>
  );
}

useGLTF.preload("/stairs.glb");
